import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Table, TableWrap, THead, TH, TR, TD } from '@/components/ui/table';
import PanelResult from './PanelResult';
import {
  CK_ROUTES,
  CK_PERMISSIONS,
  ProductionOrderStatus,
  apiMessage,
  newOperationId,
  type ProductionOrder,
  type CreateProductionOrderBody,
  type WarehouseRef,
} from '../centralKitchenApi';
import { productionStatusTone, formatDateTime, formatQuantity } from '../lib';

interface RecipeOption {
  id: string;
  name: string;
}

const EMPTY_CREATE: CreateProductionOrderBody = {
  recipeId: '',
  sourceWarehouseId: '',
  outputWarehouseId: '',
  plannedQuantity: '',
};

/**
 * Commissary batches. `DRAFT → RELEASED → COMPLETED`, and nothing leaves
 * stock until the last of those — release only pins the recipe version and
 * snapshots the plan, complete is the atomic consume-and-produce.
 */
export default function ProductionPanel() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const { confirm } = useConfirm();

  const [status, setStatus] = useState<ProductionOrderStatus | ''>('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateProductionOrderBody>(EMPTY_CREATE);
  const [completeTarget, setCompleteTarget] = useState<ProductionOrder | null>(null);
  const [producedQuantity, setProducedQuantity] = useState('');
  const [wastedQuantity, setWastedQuantity] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const listParams = useMemo(() => ({ ...(status ? { status } : {}), limit: 100 }), [status]);
  const orders = useApi<unknown>(can(CK_PERMISSIONS.productionView) ? CK_ROUTES.productionOrders : null, listParams);
  const { rows } = rowsOf<ProductionOrder>(orders.data);

  const warehouses = useApi<unknown>(createOpen ? CK_ROUTES.warehouses : null, { isActive: true, limit: 200 });
  const { rows: warehouseRows } = rowsOf<WarehouseRef>(warehouses.data);
  const recipes = useApi<unknown>(createOpen && can('inventory.view') ? '/recipes' : null, { limit: 200 });
  const { rows: recipeRows } = rowsOf<RecipeOption>(recipes.data);

  useEffect(() => {
    if (completeTarget) {
      setProducedQuantity(completeTarget.plannedQuantity);
      setWastedQuantity('');
    }
  }, [completeTarget]);

  const create = async () => {
    if (!createForm.recipeId || !createForm.sourceWarehouseId || !createForm.plannedQuantity.trim()) return;
    setBusyId('create');
    try {
      await api.post(CK_ROUTES.productionOrders, {
        ...createForm,
        outputWarehouseId: createForm.outputWarehouseId || undefined,
      });
      toast.success(t('centralKitchen.production.created'));
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE);
      orders.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('centralKitchen.states.saveFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const release = async (order: ProductionOrder) => {
    setBusyId(order.id);
    try {
      await api.post(CK_ROUTES.productionRelease(order.id), {});
      toast.success(t('centralKitchen.production.released'));
      orders.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('centralKitchen.states.saveFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const submitComplete = async () => {
    if (!completeTarget || !producedQuantity.trim()) return;
    setBusyId(completeTarget.id);
    try {
      await api.post(CK_ROUTES.productionComplete(completeTarget.id), {
        producedQuantity: producedQuantity.trim(),
        wastedQuantity: wastedQuantity.trim() || undefined,
        operationId: newOperationId('ck-production-complete'),
      });
      toast.success(t('centralKitchen.production.completed'));
      setCompleteTarget(null);
      orders.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('centralKitchen.states.saveFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (order: ProductionOrder) => {
    const ok = await confirm({
      title: t('centralKitchen.production.cancelTitle', { number: order.number }),
      confirmLabel: t('centralKitchen.production.cancelConfirm'),
      tone: 'danger',
    });
    if (!ok) return;
    setBusyId(order.id);
    try {
      await api.post(CK_ROUTES.productionCancel(order.id), {});
      toast.success(t('centralKitchen.production.cancelled'));
      orders.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('centralKitchen.states.saveFailed')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Field label={t('centralKitchen.production.filterStatus')} controlId="ck-production-status">
          <Select value={status} onChange={(e) => setStatus(e.target.value as ProductionOrderStatus | '')}>
            <option value="">{t('centralKitchen.transfers.allStatuses')}</option>
            {Object.values(ProductionOrderStatus).map((s) => (
              <option key={s} value={s}>
                {t(`centralKitchen.productionStatus.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={orders.reload}>
            <Icon as={RefreshCw} />
            {t('common.refresh')}
          </Button>
          {can(CK_PERMISSIONS.productionCreate) && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Icon as={Plus} />
              {t('centralKitchen.production.new')}
            </Button>
          )}
        </div>
      </div>

      <TableWrap>
        <PanelResult
          loading={orders.loading}
          error={orders.error}
          count={rows.length}
          onRetry={orders.reload}
          subject={t('centralKitchen.production.subject')}
          filtered={!!status}
          onClearFilters={() => setStatus('')}
          emptyTitle={t('centralKitchen.production.emptyTitle')}
          emptyHint={t('centralKitchen.production.emptyHint')}
        >
          <Table>
            <THead>
              <TR>
                <TH>{t('centralKitchen.production.columns.number')}</TH>
                <TH>{t('centralKitchen.production.columns.status')}</TH>
                <TH>{t('centralKitchen.production.columns.recipe')}</TH>
                <TH className="text-end">{t('centralKitchen.production.columns.planned')}</TH>
                <TH className="text-end">{t('centralKitchen.production.columns.produced')}</TH>
                <TH>{t('centralKitchen.production.columns.scheduled')}</TH>
                <TH />
              </TR>
            </THead>
            <tbody>
              {rows.map((order) => (
                <TR key={order.id}>
                  <TD className="font-mono text-xs">{order.number}</TD>
                  <TD>
                    <Badge tone={productionStatusTone(order.status)}>{t(`centralKitchen.productionStatus.${order.status}`)}</Badge>
                  </TD>
                  <TD>{order.recipe?.name ?? order.recipeId}</TD>
                  <TD className="text-end tabular-nums">{formatQuantity(order.plannedQuantity, i18n.language)}</TD>
                  <TD className="text-end tabular-nums">{formatQuantity(order.producedQuantity, i18n.language)}</TD>
                  <TD className="text-xs text-ink-muted">{formatDateTime(order.scheduledFor, i18n.language)}</TD>
                  <TD>
                    <div className="flex justify-end gap-1">
                      {can(CK_PERMISSIONS.productionRelease) && order.status === ProductionOrderStatus.DRAFT && (
                        <Button size="sm" variant="outline" onClick={() => release(order)} disabled={busyId === order.id}>
                          {t('centralKitchen.production.release')}
                        </Button>
                      )}
                      {can(CK_PERMISSIONS.productionComplete) && order.status === ProductionOrderStatus.RELEASED && (
                        <Button size="sm" onClick={() => setCompleteTarget(order)} disabled={busyId === order.id}>
                          {t('centralKitchen.production.complete')}
                        </Button>
                      )}
                      {can(CK_PERMISSIONS.productionCancel) && order.status === ProductionOrderStatus.DRAFT && (
                        <Button size="sm" variant="ghost" onClick={() => cancel(order)} disabled={busyId === order.id}>
                          {t('common.cancel')}
                        </Button>
                      )}
                    </div>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </PanelResult>
      </TableWrap>

      {createOpen && (
        <Dialog
          open
          onClose={() => setCreateOpen(false)}
          title={t('centralKitchen.production.new')}
          className="max-w-lg"
          footer={
            <>
              <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={busyId === 'create'}>
                {t('common.cancel')}
              </Button>
              <Button onClick={create} disabled={busyId === 'create'}>
                {t('centralKitchen.production.create')}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            {recipeRows.length > 0 ? (
              <Field label={t('centralKitchen.production.columns.recipe')} required controlId="ck-prod-recipe">
                <Select
                  value={createForm.recipeId}
                  onChange={(e) => setCreateForm((f) => ({ ...f, recipeId: e.target.value }))}
                >
                  <option value="">{t('centralKitchen.production.chooseRecipe')}</option>
                  {recipeRows.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : (
              <Field label={t('centralKitchen.production.columns.recipe')} required controlId="ck-prod-recipe-id">
                <Input
                  value={createForm.recipeId}
                  onChange={(e) => setCreateForm((f) => ({ ...f, recipeId: e.target.value }))}
                />
              </Field>
            )}

            <Field label={t('centralKitchen.production.sourceWarehouse')} required controlId="ck-prod-source">
              <Select
                value={createForm.sourceWarehouseId}
                onChange={(e) => setCreateForm((f) => ({ ...f, sourceWarehouseId: e.target.value }))}
              >
                <option value="">{t('centralKitchen.transfers.chooseWarehouse')}</option>
                {warehouseRows.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name} ({w.code})
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              label={t('centralKitchen.production.outputWarehouse')}
              hint={t('centralKitchen.production.outputWarehouseHint')}
              controlId="ck-prod-output"
            >
              <Select
                value={createForm.outputWarehouseId ?? ''}
                onChange={(e) => setCreateForm((f) => ({ ...f, outputWarehouseId: e.target.value }))}
              >
                <option value="">{t('centralKitchen.production.sameAsSource')}</option>
                {warehouseRows.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name} ({w.code})
                  </option>
                ))}
              </Select>
            </Field>

            <Field label={t('centralKitchen.production.columns.planned')} required controlId="ck-prod-planned">
              <Input
                inputMode="decimal"
                value={createForm.plannedQuantity}
                onChange={(e) => setCreateForm((f) => ({ ...f, plannedQuantity: e.target.value }))}
              />
            </Field>

            <Field label={t('centralKitchen.transfers.notes')} controlId="ck-prod-notes">
              <Textarea
                rows={2}
                value={createForm.notes ?? ''}
                onChange={(e) => setCreateForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </Field>
          </div>
        </Dialog>
      )}

      {completeTarget && (
        <Dialog
          open
          onClose={() => setCompleteTarget(null)}
          title={t('centralKitchen.production.completeTitle', { number: completeTarget.number })}
          description={t('centralKitchen.production.completeDescription')}
          className="max-w-md"
          footer={
            <>
              <Button variant="outline" onClick={() => setCompleteTarget(null)} disabled={busyId === completeTarget.id}>
                {t('common.cancel')}
              </Button>
              <Button onClick={submitComplete} disabled={busyId === completeTarget.id || !producedQuantity.trim()}>
                {t('centralKitchen.production.completeConfirm')}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <Field
              label={t('centralKitchen.production.producedQuantity')}
              hint={t('centralKitchen.production.producedQuantityHint')}
              required
              controlId="ck-prod-produced"
            >
              <Input inputMode="decimal" value={producedQuantity} onChange={(e) => setProducedQuantity(e.target.value)} />
            </Field>
            <Field
              label={t('centralKitchen.production.wastedQuantity')}
              hint={t('centralKitchen.production.wastedQuantityHint')}
              controlId="ck-prod-wasted"
            >
              <Input inputMode="decimal" value={wastedQuantity} onChange={(e) => setWastedQuantity(e.target.value)} />
            </Field>
          </div>
        </Dialog>
      )}
    </div>
  );
}
